import React, { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Loader2, MapPin, Plus, X as XIcon, Paperclip, Send, CheckCircle2, Link2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { portalApi, uploadPortalFile } from "@/lib/portalApi";
import AddressAutocomplete from "@/components/AddressAutocomplete";

const SERVICIOS = [
  { id: "montaje", label: "Montaje" },
  { id: "produccion", label: "Producción" },
  { id: "diseno", label: "Diseño" },
];

export function PortalSolicitudForm({ onCreated }) {
  const [espacios, setEspacios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [ok, setOk] = useState(null);

  const [tipo, setTipo] = useState("firme");
  const [servicios, setServicios] = useState(["montaje"]);
  const [fecha, setFecha] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [aaffUrl, setAaffUrl] = useState("");
  const [seleccion, setSeleccion] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [nuevos, setNuevos] = useState([]);
  const [nuevoNombre, setNuevoNombre] = useState("");
  const [nuevoCiudad, setNuevoCiudad] = useState("");
  const [nuevoExtra, setNuevoExtra] = useState(null); // {direccion, provincia, latitud, longitud}
  const [mostrarNuevo, setMostrarNuevo] = useState(false);
  const [adjuntos, setAdjuntos] = useState([]);
  const [subiendo, setSubiendo] = useState(false);
  const [solNombre, setSolNombre] = useState("");
  const [solEmail, setSolEmail] = useState("");
  const [solMovil, setSolMovil] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    portalApi().get("/portal/solicitudes/espacios")
      .then(({ data }) => setEspacios(data))
      .catch(() => toast.error("Error cargando espacios"))
      .finally(() => setLoading(false));
  }, []);

  const toggleServicio = (id) =>
    setServicios((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleEspacio = (id) =>
    setSeleccion((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const addNuevo = () => {
    if (!nuevoNombre.trim()) return;
    setNuevos((n) => [...n, {
      nombre: nuevoNombre.trim(),
      ciudad: nuevoCiudad.trim() || null,
      ...(nuevoExtra || {}),
    }]);
    setNuevoNombre(""); setNuevoCiudad(""); setNuevoExtra(null); setMostrarNuevo(false);
  };

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setSubiendo(true);
    for (const f of files) {
      try {
        const adj = await uploadPortalFile(f);
        setAdjuntos((a) => [...a, adj]);
      } catch {
        toast.error(`Error subiendo ${f.name}`);
      }
    }
    setSubiendo(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const enviar = async () => {
    if (!solNombre.trim()) return toast.error("Indica tu nombre");
    if (!solEmail.trim() || !solEmail.includes("@")) return toast.error("Indica un email válido");
    if (!descripcion.trim()) return toast.error("Describe el trabajo que necesitas");
    if (!seleccion.length && !nuevos.length) return toast.error("Selecciona al menos un espacio");
    if (!servicios.length) return toast.error("Selecciona al menos un servicio");
    setEnviando(true);
    try {
      const { data } = await portalApi().post("/portal/solicitudes", {
        tipo_solicitud: tipo,
        servicios,
        fecha_montaje: fecha || null,
        descripcion,
        aaff_url: aaffUrl.trim() || null,
        espacios_ids: seleccion,
        espacios_nuevos: nuevos,
        adjuntos,
        solicitante_nombre: solNombre.trim(),
        solicitante_email: solEmail.trim(),
        solicitante_movil: solMovil.trim() || null,
      });
      setOk(data);
      onCreated?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error enviando la solicitud");
    } finally {
      setEnviando(false);
    }
  };

  if (ok) {
    return (
      <div className="text-center py-12 max-w-md mx-auto" data-testid="solicitud-ok">
        <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto mb-4" />
        <h2 className="text-lg font-bold mb-1">¡Solicitud enviada!</h2>
        <p className="text-sm text-zinc-400 mb-1">
          Hemos registrado tu solicitud con el código <b className="text-amber-300 font-mono">{ok.codigo}</b>.
        </p>
        <p className="text-xs text-zinc-500 mb-5">Te hemos enviado una copia por email. Nuestro equipo la revisará y te contactará en breve.</p>
        <Button data-testid="solicitud-otra-btn" onClick={() => {
          setOk(null); setDescripcion(""); setSeleccion([]); setNuevos([]); setAdjuntos([]); setFecha(""); setAaffUrl("");
        }} className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">
          Crear otra solicitud
        </Button>
      </div>
    );
  }

  const espaciosFiltrados = espacios.filter((e) =>
    !busqueda.trim() || `${e.nombre} ${e.ciudad || ""}`.toLowerCase().includes(busqueda.toLowerCase()));

  return (
    <div className="max-w-2xl space-y-5" data-testid="solicitud-form">
      {/* Tipo */}
      <div>
        <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">¿Qué necesitas?</div>
        <div className="grid grid-cols-2 gap-2">
          {[["firme", "Petición en firme", "Trabajo confirmado, adelante con él"],
            ["presupuesto", "Pedir presupuesto", "Quiero saber el coste primero"]].map(([v, l, d]) => (
            <button key={v} onClick={() => setTipo(v)} data-testid={`solicitud-tipo-${v}`}
              className={`text-left rounded-xl border p-3 transition-colors ${tipo === v
                ? "border-amber-500 bg-amber-500/10" : "border-zinc-800 bg-zinc-900/50 hover:border-zinc-700"}`}>
              <div className={`text-sm font-semibold ${tipo === v ? "text-amber-300" : "text-zinc-200"}`}>{l}</div>
              <div className="text-[11px] text-zinc-500 mt-0.5">{d}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Servicios */}
      <div>
        <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Servicios</div>
        <div className="flex gap-2 flex-wrap">
          {SERVICIOS.map((s) => (
            <button key={s.id} onClick={() => toggleServicio(s.id)} data-testid={`solicitud-servicio-${s.id}`}
              className={`px-4 py-2 rounded-full text-sm border transition-colors ${servicios.includes(s.id)
                ? "border-amber-500 bg-amber-500/15 text-amber-300 font-semibold"
                : "border-zinc-800 bg-zinc-900/50 text-zinc-400 hover:border-zinc-700"}`}>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Fecha */}
      <div>
        <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Fecha de montaje deseada</div>
        <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)}
          data-testid="solicitud-fecha" className="bg-zinc-900 border-zinc-800 w-48" />
      </div>

      {/* Espacios */}
      <div>
        <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2 flex items-center justify-between">
          <span>Espacios ({seleccion.length + nuevos.length} seleccionados)</span>
          <button onClick={() => setMostrarNuevo(!mostrarNuevo)} data-testid="solicitud-nuevo-espacio-btn"
            className="text-amber-400 hover:underline flex items-center gap-1 normal-case tracking-normal">
            <Plus className="w-3 h-3" /> Añadir espacio nuevo
          </button>
        </div>
        {mostrarNuevo && (
          <div className="mb-2 bg-zinc-900/60 border border-amber-500/30 rounded-lg p-3 space-y-2">
            <div className="text-[11px] text-zinc-500">🔍 Busca el establecimiento en Google y se rellenará solo (nombre, dirección, ciudad…)</div>
            <AddressAutocomplete
              value={nuevoNombre}
              onChange={setNuevoNombre}
              apiClient={portalApi()}
              endpoint="/portal/places/autocomplete"
              placeholder="Ej. Primor Nervión Sevilla"
              data-testid="solicitud-nuevo-nombre"
              className="bg-zinc-950 border-zinc-800 text-sm"
              onSelect={(sel) => {
                setNuevoNombre(sel.raw?.name || sel.direccion || "");
                setNuevoCiudad(sel.ciudad || "");
                setNuevoExtra({
                  direccion: sel.direccion || null,
                  provincia: sel.provincia || null,
                  latitud: sel.latitud ?? null,
                  longitud: sel.longitud ?? null,
                });
              }}
            />
            {nuevoExtra?.direccion && (
              <div className="text-[11px] text-emerald-300 flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {nuevoExtra.direccion}
              </div>
            )}
            <div className="flex gap-2">
              <Input value={nuevoCiudad} onChange={(e) => setNuevoCiudad(e.target.value)}
                placeholder="Ciudad (opcional)" data-testid="solicitud-nuevo-ciudad"
                className="bg-zinc-950 border-zinc-800 text-sm flex-1" />
              <Button onClick={addNuevo} data-testid="solicitud-nuevo-guardar"
                className="bg-amber-500 hover:bg-amber-400 text-zinc-900 text-xs font-semibold">Añadir</Button>
            </div>
          </div>
        )}
        {nuevos.length > 0 && (
          <div className="flex gap-1.5 flex-wrap mb-2">
            {nuevos.map((n, i) => (
              <span key={i} className="inline-flex items-center gap-1 text-xs bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 rounded-full px-2.5 py-1">
                <MapPin className="w-3 h-3" /> {n.nombre} (nuevo)
                <button onClick={() => setNuevos((x) => x.filter((_, j) => j !== i))}><XIcon className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
        )}
        {loading ? (
          <div className="text-zinc-500 text-sm py-3"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Cargando espacios…</div>
        ) : (
          <>
            {espacios.length > 6 && (
              <Input value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar espacio…" data-testid="solicitud-buscar-espacio"
                className="bg-zinc-900 border-zinc-800 text-sm mb-2" />
            )}
            <div className="max-h-56 overflow-y-auto space-y-1 pr-1" data-testid="solicitud-espacios-list">
              {espaciosFiltrados.map((e) => (
                <button key={e.id} onClick={() => toggleEspacio(e.id)}
                  data-testid={`solicitud-espacio-${e.id}`}
                  className={`w-full text-left rounded-lg border px-3 py-2 text-sm flex items-center gap-2 transition-colors ${seleccion.includes(e.id)
                    ? "border-amber-500 bg-amber-500/10 text-amber-200"
                    : "border-zinc-800 bg-zinc-900/40 text-zinc-300 hover:border-zinc-700"}`}>
                  <MapPin className="w-3.5 h-3.5 shrink-0 opacity-60" />
                  <span className="flex-1 truncate">{e.nombre}</span>
                  {e.ciudad && <span className="text-[11px] text-zinc-500">{e.ciudad}</span>}
                </button>
              ))}
              {!espaciosFiltrados.length && (
                <div className="text-xs text-zinc-500 py-2">No hay espacios{busqueda ? " que coincidan" : " registrados aún — añade uno nuevo"}.</div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Descripción */}
      <div>
        <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Descripción del trabajo *</div>
        <Textarea value={descripcion} onChange={(e) => setDescripcion(e.target.value)} rows={4}
          placeholder="Cuéntanos qué necesitas: campaña, medidas, materiales, referencias…"
          data-testid="solicitud-descripcion" className="bg-zinc-900 border-zinc-800 text-sm" />
      </div>

      {/* AAFF + Adjuntos */}
      <div>
        <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Archivos de referencia</div>
        <div className="flex items-center gap-2 mb-2">
          <Link2 className="w-4 h-4 text-zinc-500 shrink-0" />
          <Input value={aaffUrl} onChange={(e) => setAaffUrl(e.target.value)}
            placeholder="Enlace AAFF (WeTransfer, Drive…)" data-testid="solicitud-aaff-url"
            className="bg-zinc-900 border-zinc-800 text-sm" />
        </div>
        <input ref={fileRef} type="file" multiple className="hidden"
          accept="image/*,.pdf,.ppt,.pptx" onChange={onFiles} data-testid="solicitud-file-input" />
        <button onClick={() => fileRef.current?.click()} disabled={subiendo}
          data-testid="solicitud-adjuntar-btn"
          className="text-sm text-amber-400 hover:underline flex items-center gap-1.5">
          {subiendo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
          {subiendo ? "Subiendo…" : "Adjuntar render / PDF / JPG / PPT"}
        </button>
        {adjuntos.length > 0 && (
          <div className="flex gap-1.5 flex-wrap mt-2">
            {adjuntos.map((a, i) => (
              <span key={i} className="inline-flex items-center gap-1 text-xs bg-zinc-800 text-zinc-300 rounded-full px-2.5 py-1">
                <Paperclip className="w-3 h-3" /> {a.nombre}
                <button onClick={() => setAdjuntos((x) => x.filter((_, j) => j !== i))}><XIcon className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Datos del solicitante */}
      <div>
        <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Tus datos (recibirás copia por email) *</div>
        <div className="space-y-2">
          <Input value={solNombre} onChange={(e) => setSolNombre(e.target.value)}
            placeholder="Nombre y apellidos *" data-testid="solicitud-sol-nombre"
            className="bg-zinc-900 border-zinc-800 text-sm" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Input type="email" value={solEmail} onChange={(e) => setSolEmail(e.target.value)}
              placeholder="Email *" data-testid="solicitud-sol-email"
              className="bg-zinc-900 border-zinc-800 text-sm" />
            <Input type="tel" value={solMovil} onChange={(e) => setSolMovil(e.target.value)}
              placeholder="Móvil" data-testid="solicitud-sol-movil"
              className="bg-zinc-900 border-zinc-800 text-sm" />
          </div>
        </div>
      </div>

      <Button onClick={enviar} disabled={enviando} data-testid="solicitud-enviar-btn"
        className="w-full bg-amber-500 hover:bg-amber-400 text-zinc-900 font-bold py-5">
        {enviando ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
        {tipo === "presupuesto" ? "Solicitar presupuesto" : "Enviar solicitud de trabajo"}
      </Button>
    </div>
  );
}
